import React, { useState, useEffect } from 'react';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { useNavigate } from 'react-router-dom';
import './ReportedJob.css'; // Using the same CSS
import Swal from 'sweetalert2';
import { FaTrash, FaEye } from 'react-icons/fa';
import BaseUrl from '../../ApiServices/BaseUrl';

function ReportedJob() {
  const [reportedJobs, setReportedJobs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch reported jobs
        const reportedResponse = await fetch(`${BaseUrl}/api/jobreport`);
        if (!reportedResponse.ok) {
          throw new Error('Failed to fetch reported jobs');
        }
        const reportedData = await reportedResponse.json();

        // Fetch all jobs (to check active status)
        const jobsResponse = await fetch(`${BaseUrl}/api/employer/all`);
        if (!jobsResponse.ok) {
          throw new Error('Failed to fetch jobs');
        }
        const jobsData = await jobsResponse.json();

        setReportedJobs(reportedData);
        setJobs(jobsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleReview = (jobId) => {
    navigate(`/reportedjobview/${jobId}`);
  };

  const handleView = (jobId) => {
    navigate(`/reportedjobview/${jobId}`);
  };

  const handleDelete = async (reportId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${BaseUrl}/api/jobreport/${reportId}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          throw new Error('Failed to delete report');
        }

        // Remove the deleted report from state
        setReportedJobs(reportedJobs.filter(report => report.id !== reportId));

        Swal.fire(
          'Deleted!',
          'The report has been deleted.',
          'success'
        );
      } catch (err) {
        Swal.fire(
          'Error!',
          err.message,
          'error'
        );
      }
    }
  };

  // Check if a job still exists
  const isJobActive = (jobId) => {
    return jobs.some(job => job.id === jobId);
  };

  // Get job status from database
  const getJobStatus = (jobId) => {
    const job = jobs.find(job => job.id === jobId);
    return job ? job.status : 'Active'; // Default to Active if no status found
  };

  // Get display status - ONLY Active and Inactive
  const getDisplayStatus = (jobId) => {
    const isActive = isJobActive(jobId);
    const jobStatus = getJobStatus(jobId);

    if (!isActive) {
      return 'Inactive';
    }

    // Return the actual status from database (Active or Inactive)
    return jobStatus;
  };

  // Get status class for styling - ONLY Active and Inactive
  const getStatusClass = (jobId) => {
    const displayStatus = getDisplayStatus(jobId);

    if (displayStatus === 'Active') {
      return 'status-active';
    } else {
      return 'status-inactive';
    }
  };

  if (loading) {
    return (
      <>
        <AdminNavbar />
        <div style={{ marginTop: '100px' }}>Loading...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminNavbar />
        <div style={{ marginTop: '100px' }}>Error: {error}</div>
      </>
    );
  }

  return (
    <>
      <AdminNavbar />
      <div className="reported-candidates-container">
        <h2>Reported Jobs</h2>
        {reportedJobs.length > 0 ? (
          <div className="table-wrapper">
            <div className="table-responsive-reportedcandidates">
              <table className="reported-candidates-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Reported By</th>
                    <th>Job Title</th>
                    <th>Company</th>
                    <th>Reason</th>
                    <th className="hide-on-mobile">Description</th>
                    <th>Actions</th> {/* ❌ Status heading removed */}
                  </tr>
                </thead>

                <tbody>
                  {reportedJobs.map((report, index) => {
                    const isActive = isJobActive(report.job_id);
                    const jobStatus = getJobStatus(report.job_id);
                    const displayStatus = getDisplayStatus(report.job_id);
                    const statusClass = getStatusClass(report.job_id);

                    const job = jobs.find(job => job.id === report.job_id);

                    return (
                      <tr key={report.id}>
                        <td>{index + 1}</td>
                        <td>{report.first_name}</td>
                        <td>{job ? job.job_title : `Job ID: ${report.job_id}`}</td>
                        <td>{job ? job.company_name : "N/A"}</td>
                        <td>{report.reason}</td>
                        <td className="hide-on-mobile">{report.description}</td>

                        {/* Updated Actions column with Status inside */}
                        <td>
                          <div className="actions-container">

                            <button
                              className="view-btn"
                              style={{ width: "82px" }}
                              onClick={() => handleView(report.job_id)}
                              title="View Job"
                            >
                              View
                            </button>

                            {!isActive ? (
                              <button className="removed-btn" disabled>
                                Removed
                              </button>
                            ) : jobStatus === "Inactive" ? (
                              <button className="reviewed-btn" disabled>
                                Reviewed
                              </button>
                            ) : (
                              <button
                                className="review-btn"
                                onClick={() => handleReview(report.job_id)}
                              >
                                Review
                              </button>
                            )}

                            {/* ✅ Status moved under Actions */}
                            <span
                              className={`report-status-badge ${statusClass}`}
                              style={{ marginTop: "0px", display: "inline-block" }}
                            >
                              {displayStatus}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

            </div>
          </div>
        ) : (
          <p>No reported jobs found.</p>
        )}
      </div>
    </>
  );
}

export default ReportedJob;